import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    DndContext,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    type DragEndEvent,
    type DragStartEvent,
    type DragMoveEvent,
    DragOverlay,
} from '@dnd-kit/core';
import * as Popover from '@radix-ui/react-popover';
import { AlertCircle, Loader2 } from 'lucide-react';
import { addDays, differenceInCalendarDays, startOfDay } from 'date-fns';
import type { CustomField, SavedView, Task } from '@/lib/schemas';
import { useI18n } from '@/lib/i18n';
import { useToast } from '@/hooks/use-toast';
import { useUpdateSavedView } from '@/hooks/use-views';
import { apiGet } from '@/lib/api';
import {
    useUpdateTaskDates,
    getSprintBands,
} from '@/hooks/use-gantt';
import {
    normaliseGanttConfig,
    resolveTimeScale,
    type NormalisedGanttConfig,
} from './gantt-config';
import { getStatusLabel } from './gantt-i18n';
import { applyGanttFilters } from './gantt-filters';
import { GanttToolbar } from './gantt-toolbar';
import { GanttSettingsPanel } from './gantt-settings-panel';
import { GanttTimeline, type GanttDragPreview } from './gantt-timeline';
import { useCreateTaskDependency } from '@/hooks/use-task-dependencies';

// ============================================
// GANTT VIEW
// ============================================
//
// t17: top-level Gantt component. Owns the anchor date, the
// (locally-overridden) view config, the settings popover state and
// the drag-to-reschedule wiring. Mirrors CalendarView's saved-view-id
// split (t16) so the *config* we render is derived from the live
// `activeView` prop — we only persist when the user changes a setting
// or explicitly saves the view via the toolbar.
//
// When the user drags a bar to reschedule we PATCH /api/tasks/:id
// with new startDate / endDate (see useUpdateTaskDates). The mutation
// is optimistic with rollback on error — same pattern as the Calendar
// drag-to-update.
//
// R3 / t17: track two new pieces of ephemeral UI state —
// `expandedTaskIds` (which task rows have their chevron open so the
// subtask list is inline) and `dragPreview` (the live in-progress
// drag geometry so the bar visibly grows/shrinks under the cursor).

export interface GanttViewProps {
    projectId: string;
    tasks: Task[];
    isLoading: boolean;
    error?: Error | null;
    customFields: CustomField[];
    activeView?: SavedView | null;
    onTaskClick?: (task: Task) => void;
    canEdit?: boolean;
}

// Helper: ms per unit at a concrete time scale. Mirrors the px/unit
// table in gantt-config.ts (Day=1d, Week=7d, Month≈30d). Used to
// translate a drag delta in pixels into a date delta.
const UNIT_MS: Record<'day' | 'week' | 'month', number> = {
    day: 24 * 60 * 60 * 1000,
    week: 7 * 24 * 60 * 60 * 1000,
    month: 30 * 24 * 60 * 60 * 1000,
};

function snapToDay(d: Date): Date {
    return startOfDay(d);
}

// R6 / t31: small helper used by the dep-picker click handler
// to figure out which of two tasks is leftmost on the timeline.
// We map the task's start date into timeline-local pixels
// using the same Day math the bar renderer uses. For
// dateless tasks we treat them as starting at the timeline's
// origin (left edge) so they sit on top of the dep direction
// decision rather than behind it.
function getBarLeftPx(task: Task | undefined): number {
    if (!task) return Number.POSITIVE_INFINITY;
    const sd = (task as any).startDate as string | null | undefined;
    if (!sd) return 0;
    const d = new Date(sd);
    if (Number.isNaN(d.getTime())) return 0;
    return Math.max(0, differenceInCalendarDays(d, new Date()));
}

export function GanttView({
    projectId,
    tasks,
    isLoading,
    error,
    customFields,
    activeView,
    onTaskClick,
    canEdit,
}: GanttViewProps) {
    const { t } = useI18n();
    const { toast } = useToast();

    // R9 / t36: subtask expansion removed entirely. The user spec
    // asked us to delete the chevron — the Gantt timeline now
    // only renders root tasks. The previous block (the
    // `requestedSubtaskParentIds` set, the `useQueries` per-parent
    // subtask fetcher, and the `subtasksMap` that fed into
    // `childrenByParentId`) is gone. `expandedTaskIds`,
    // `handleToggleExpanded`, and the sync useEffect are also
    // removed below.

    // Resolve the saved view's config + a small bit of local override
    // (origin, settings popover open state, drag overlay). The
    // "currentConfig" is derived so we never copy server data into a
    // frozen useState copy.
    const savedConfig = useMemo(
        () => normaliseGanttConfig(activeView?.config),
        [activeView?.config]
    );
    const [origin, setOrigin] = useState<Date>(() => {
        // Default the origin to "today" minus a small padding so the
        // earliest task has some breathing room on the left.
        return startOfDay(addDays(new Date(), -3));
    });
    // R7 / t33: the toolbar buttons + cog only worked when an active
    // saved view existed (handleConfigChange early-returned otherwise,
    // so the change never reached the rendered config). Mirror the
    // CalendarView R4 fix: keep a local override that always reflects
    // the latest user choice, and clear it when the active view id
    // changes (so stale state never bleeds across views).
    const [localOverride, setLocalOverride] = useState<NormalisedGanttConfig | null>(
        null
    );
    const activeViewId = activeView?.id ?? null;
    useEffect(() => {
        setLocalOverride(null);
    }, [activeViewId]);
    const effectiveConfig = localOverride ?? savedConfig;
    const [activeDragTaskId, setActiveDragTaskId] = useState<string | null>(null);
    // R9 / t36: subtask expansion state removed (chevron deleted).

    // R3 / t17: live drag preview geometry. Null when no drag is
    // active. Set in onDragMove (every mousemove during the drag)
    // and cleared in onDragEnd / onDragCancel.
    const [dragPreview, setDragPreview] = useState<GanttDragPreview | null>(null);
    // R7 / t33: removed `settingsAnchorRef` — the Gantt settings
    // popover now follows Calendar's R4 pattern: a single <Popover.Root>
    // wrapping the toolbar + panel, with the cog button as
    // <Popover.Trigger asChild>. Radix Popper reads its anchor from
    // the PopperContext (set by Trigger), NOT from a Content prop —
    // the previous `anchor={anchorRef.current}` hack silently failed
    // and rendered the panel off-screen.

    // R6 / t31: cursor-driven "pick dependency target" mode,
    // simplified. Single option in the menu ("Add a dependency
    // on this task"). State machine: idle → pickingTarget →
    // idle. The dep is always FS (Finish → Start), with the
    // predecessor being whichever of the two endpoints is
    // visually leftmost on the timeline. Source = right-clicked
    // task. Target = clicked task. No more bidirectional
    // arrows or direction picker dialog.
    const [pickState, setPickState] = useState<'idle' | 'pickingTarget'>('idle');
    const [pickSourceTaskId, setPickSourceTaskId] = useState<string | null>(null);
    const [pickCursorPos, setPickCursorPos] = useState<{ x: number; y: number } | null>(null);

    // Mutations
    const updateSavedView = useUpdateSavedView(projectId);
    const updateTaskDates = useUpdateTaskDates(projectId);
    const createDependency = useCreateTaskDependency(projectId);

    // Sprint payload — only present when the project has a SPRINT
    // custom field.
    const sprintPayload = useMemo(() => getSprintBands(customFields), [customFields]);
    const hasSprintField = sprintPayload.field !== null;

    // When the user switches active views, keep the origin (the
    // timeline anchor is local UI state, not persisted). When they
    // toggle a different groupBy / time scale, the config-change
    // callback below persists it.
    //
    // R9 / t36: the previous "drop expand-state on view change"
    // useEffect was removed alongside the chevron — there's no
    // expand state to reset anymore.
    useEffect(() => {
        // intentional no-op (kept as a placeholder for any future
        // per-view UI resets that don't belong in the config).
    }, [activeView?.id]);

    // Auto-jump origin to the earliest task start when the task list
    // first arrives so a user with a project whose tasks all start
    // months ago doesn't see an empty timeline.
    const initialOriginSetRef = useRef(false);
    useEffect(() => {
        if (initialOriginSetRef.current) return;
        if (!tasks || tasks.length === 0) return;
        const starts: number[] = [];
        for (const task of tasks) {
            const sd = (task as any).startDate as string | null | undefined;
            if (sd) {
                const d = new Date(sd);
                if (!Number.isNaN(d.getTime())) starts.push(d.getTime());
            }
        }
        if (starts.length === 0) return;
        const earliest = Math.min(...starts);
        const candidate = startOfDay(addDays(new Date(earliest), -3));
        setOrigin((prev) => (prev.getTime() === candidate.getTime() ? prev : candidate));
        initialOriginSetRef.current = true;
    }, [tasks]);

    // R9 / t36: subtask expansion removed (chevron deleted) — we
    // no longer fetch subtasks per-parent or merge them into a
    // childrenByParentId map. The timeline only renders root
    // tasks; `filteredTasks` here is the final source of truth.

    // Filtered task list (after gantt-side filters).
    const filteredTasks = useMemo(
        () => applyGanttFilters(tasks, effectiveConfig.filters, customFields),
        [tasks, effectiveConfig.filters, customFields]
    );

    // Persist a config change by PATCHing the active view. Mirrors the
    // Kanban "Columns + Card fields" + Calendar "viewMode + dateSource"
    // pattern: every change writes immediately so the user doesn't
    // lose work on reload.
    //
    // R7 / t33: ALWAYS reflect the change locally first
    // (`setLocalOverride`) so the toolbar / settings panel update
    // instantly — even when there's no active view to PATCH. The
    // previous code early-returned without updating state, which made
    // the Day / Week / Month / Auto buttons appear "dead" whenever
    // the user landed on Gantt with activeView === null. Only the
    // mutation path is gated on `activeView` existing.
    const handleConfigChange = useCallback(
        (next: NormalisedGanttConfig) => {
            setLocalOverride(next);
            if (!activeView || !canEdit) return;
            const merged = {
                ...(activeView.config || {}),
                timeScale: next.timeScale,
                filters: next.filters,
                showSprints: next.showSprints,
                ganttGroupBy: next.groupBy,
                // R3 / t17: persist showDateless so the toggle survives
                // reload. We cast through any because the backend's
                // viewConfigSchema doesn't enumerate this field yet —
                // once the schema is extended we'll drop the cast.
                showDateless: next.showDateless,
            };
            updateSavedView.mutate({ viewId: activeView.id, data: { config: merged } });
        },
        [activeView, updateSavedView, canEdit]
    );

    // R6 / t31: start the cursor-driven dep picker from the
    // row's context menu (the "Add a dependency on this task"
    // item). The signature still takes a `direction` parameter
    // so the existing row prop stays compatible, but we ignore
    // it — every dep is FS and predecessor/ successor are
    // computed at POST time from the two endpoints' X order.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleStartPickTarget = useCallback(
        (_direction: 'outgoing' | 'incoming') => (taskId: string) => {
            if (!canEdit) return;
            setPickSourceTaskId(taskId);
            setPickState('pickingTarget');
            setPickCursorPos(null);
        },
        [canEdit]
    );

    const handleCancelPick = useCallback(() => {
        setPickState('idle');
        setPickSourceTaskId(null);
        setPickCursorPos(null);
    }, []);

    // R4 / t22: global mousemove + click + contextmenu listeners
    // while in pickingTarget mode. We listen on document rather
    // than on the timeline div so the cursor can travel outside the
    // timeline bounds (e.g. over the toolbar) without losing
    // tracking. Cleanup removes them when pickState flips back.
    useEffect(() => {
        if (pickState === 'pickingTarget') {
            document.body.style.cursor = 'crosshair';
            return () => {
                document.body.style.cursor = '';
            };
        }
        return;
    }, [pickState]);

    // R4 / t22: global mousemove + click + contextmenu listeners
    // while in pickingTarget mode. We listen on document rather
    // than on the timeline div so the cursor can travel outside the
    // timeline bounds (e.g. over the toolbar) without losing
    // tracking. Cleanup removes them when pickState flips back.
    useEffect(() => {
        if (pickState !== 'pickingTarget') return;

        const onMove = (e: MouseEvent) => {
            setPickCursorPos({ x: e.clientX, y: e.clientY });
        };

        const onContextMenu = (e: MouseEvent) => {
            // Right-click anywhere while picking: cancel.
            e.preventDefault();
            handleCancelPick();
        };

        const onClick = (e: MouseEvent) => {
            // Find the row the user clicked on by walking up from the
            // click target until we hit a `[data-row-task-id]`.
            const target = e.target as HTMLElement | null;
            const rowEl = target?.closest('[data-row-task-id]') as HTMLElement | null;
            const targetTaskId = rowEl?.dataset.rowTaskId;
            if (!targetTaskId) {
                // Clicked on the timeline background or outside any
                // row — treat as cancel.
                handleCancelPick();
                return;
            }
            if (
                !pickSourceTaskId ||
                targetTaskId === pickSourceTaskId
            ) {
                // Same task as source, or no source selected — cancel.
                handleCancelPick();
                return;
            }
            // R6 / t31: every dep goes LEFT-to-RIGHT visually.
            // We compute the predecessor/ successor by reading
            // the bar geometry from the timeline's state — the
            // task on the LEFT is the predecessor, the task on
            // the RIGHT is the successor. If the two tasks
            // happen to share an X (overlapping bars) we fall
            // back to the source-as-predecessor convention.
            const sourceTask = filteredTasks.find((tk) => tk.id === pickSourceTaskId);
            const targetTask = filteredTasks.find((tk) => tk.id === targetTaskId);
            const sourceLeft = sourceTask ? getBarLeftPx(sourceTask) : Infinity;
            const targetLeft = targetTask ? getBarLeftPx(targetTask) : Infinity;
            const sourceOnLeft = sourceLeft <= targetLeft;
            const predecessorId = sourceOnLeft ? pickSourceTaskId : targetTaskId;
            const successorId = sourceOnLeft ? targetTaskId : pickSourceTaskId;
            const type: 'FS' = 'FS';
            createDependency.mutate(
                { predecessorId, successorId, type },
                {
                    onSuccess: () => {
                        handleCancelPick();
                    },
                    onError: (err) => {
                        toast({
                            title: t('common.error'),
                            description: err instanceof Error ? err.message : 'Failed to create dependency',
                            variant: 'destructive',
                        });
                        handleCancelPick();
                    },
                }
            );
        };

        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                handleCancelPick();
            }
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('click', onClick, true);
        document.addEventListener('contextmenu', onContextMenu, true);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('click', onClick, true);
            document.removeEventListener('contextmenu', onContextMenu, true);
            document.removeEventListener('keydown', onKey);
        };
    }, [
        pickState,
        pickSourceTaskId,
        filteredTasks,
        createDependency,
        handleCancelPick,
        t,
        toast,
    ]);

    // R9 / t36: `handleToggleExpanded` and the sync useEffect
    // removed — there are no longer expanded rows or per-parent
    // subtask fetches to coordinate.

    // dnd-kit sensors. PointerSensor with a tiny activation distance so
    // click events still fire (mirrors CalendarView + Kanban).
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor)
    );

    const handleDragStart = useCallback((event: DragStartEvent) => {
        const data = event.active.data.current as any;
        if (data?.type === 'gantt-bar') {
            setActiveDragTaskId(String(data.taskId));
        }
    }, []);

    const handleDragCancel = useCallback(() => {
        setActiveDragTaskId(null);
        setDragPreview(null);
    }, []);

    // Resolve the time scale (auto → concrete) based on the visible
    // window. We compute it here so the toolbar + timeline always
    // agree on the actual px/unit value.
    //
    // R8 / t34: the auto branch previously hardcoded the visible
    // span to 6 * UNIT_MS.month, which meant clicking Auto always
    // resolved to 'month' — the Auto button appeared to do nothing
    // because it behaved identically to clicking Month. We now
    // compute the span from the actual task dates so Auto picks the
    // best zoom to fit every task in the visible window.
    const concreteTimeScale = useMemo(() => {
        const requested = effectiveConfig.timeScale;
        if (requested !== 'auto') {
            const ms = (() => {
                if (requested === 'day') return 28 * UNIT_MS.day;
                if (requested === 'week') return 12 * UNIT_MS.week;
                if (requested === 'month') return 6 * UNIT_MS.month;
                return 6 * UNIT_MS.month;
            })();
            return resolveTimeScale(requested, ms);
        }
        // Auto: compute the actual span from filteredTasks (already
        // passed through gantt-side filters). Dateless tasks are
        // skipped — they don't contribute to the span.
        if (filteredTasks.length === 0) return 'week';
        let minTime = Number.POSITIVE_INFINITY;
        let maxTime = Number.NEGATIVE_INFINITY;
        for (const task of filteredTasks) {
            const sd = (task as any).startDate as string | null | undefined;
            const ed = (task as any).endDate as string | null | undefined;
            if (sd) {
                const d = new Date(sd).getTime();
                if (!Number.isNaN(d)) {
                    minTime = Math.min(minTime, d);
                    maxTime = Math.max(maxTime, d);
                }
            }
            if (ed) {
                const d = new Date(ed).getTime();
                if (!Number.isNaN(d)) {
                    minTime = Math.min(minTime, d);
                    maxTime = Math.max(maxTime, d);
                }
            }
        }
        if (
            !Number.isFinite(minTime) ||
            !Number.isFinite(maxTime) ||
            maxTime <= minTime
        ) {
            return 'week';
        }
        // Same thresholds as resolveTimeScale — ≤14 days → day,
        // ≤90 days → week, else month.
        const spanDays = (maxTime - minTime) / UNIT_MS.day;
        if (spanDays <= 14) return 'day';
        if (spanDays <= 90) return 'week';
        return 'month';
    }, [effectiveConfig.timeScale, filteredTasks]);

    // Shared helper: convert a drag pixel-delta at the resolved time
    // scale into a day delta (rounded to the nearest day). Same math
    // for both the live preview and the final commit — keeps them in
    // lockstep so the bar snaps to the exact position the preview
    // showed on drop.
    const computeDayDelta = useCallback(
        (deltaX: number): number => {
            const pxPerUnit =
                concreteTimeScale === 'day'
                    ? 24
                    : concreteTimeScale === 'week'
                    ? 120
                    : 180;
            const msPerPx = UNIT_MS[concreteTimeScale] / pxPerUnit;
            const msDelta = deltaX * msPerPx;
            return Math.round(msDelta / UNIT_MS.day);
        },
        [concreteTimeScale]
    );

    // Shared helper: apply a drag delta to a task's start/end dates
    // for a given mode. Returns the new start/end (any may be null for
    // the not-set case). Returns null when the mode would produce an
    // invalid range (start > end) so the caller can early-exit.
    const computePreviewDates = useCallback(
        (
            task: Task,
            mode: 'move' | 'resize-start' | 'resize-end',
            dayDelta: number
        ): { start: Date | null; end: Date | null } | null => {
            const startIso = (task as any).startDate as string | null | undefined;
            const endIso = (task as any).endDate as string | null | undefined;
            const start = startIso ? new Date(startIso) : null;
            const end = endIso ? new Date(endIso) : null;
            if (!start && !end) return null;

            let nextStart = start;
            let nextEnd = end;

            if (mode === 'move') {
                if (start) nextStart = snapToDay(addDays(start, dayDelta));
                if (end) nextEnd = snapToDay(addDays(end, dayDelta));
            } else if (mode === 'resize-start') {
                if (start) {
                    const newStart = snapToDay(addDays(start, dayDelta));
                    if (end && newStart.getTime() > end.getTime()) return null;
                    nextStart = newStart;
                } else if (end) {
                    const newStart = snapToDay(addDays(end, dayDelta));
                    if (newStart.getTime() > end.getTime()) return null;
                    nextStart = newStart;
                }
            } else if (mode === 'resize-end') {
                if (end) {
                    const newEnd = snapToDay(addDays(end, dayDelta));
                    if (start && newEnd.getTime() < start.getTime()) return null;
                    nextEnd = newEnd;
                } else if (start) {
                    const newEnd = snapToDay(addDays(start, dayDelta));
                    if (newEnd.getTime() < start.getTime()) return null;
                    nextEnd = newEnd;
                }
            }
            return { start: nextStart, end: nextEnd };
        },
        []
    );

    // R3 / t17: live drag preview. On every mousemove during a drag
    // we recompute the would-be new dates and stash them so the
    // timeline can re-render the bar with the live geometry. Throttle
    // to avoid React re-rendering on every pixel — coalesce to once
    // per animation frame so the bar visibly tracks the cursor at
    // 60fps without blowing the render budget.
    const rafRef = useRef<number | null>(null);
    const handleDragMove = useCallback(
        (event: DragMoveEvent) => {
            const dragData = event.active.data.current as any;
            if (!dragData || dragData.type !== 'gantt-bar') return;
            const taskId = String(dragData.taskId);
            const mode = dragData.mode as 'move' | 'resize-start' | 'resize-end';
            const task = filteredTasks.find((tk) => tk.id === taskId);
            if (!task) return;
            const dayDelta = computeDayDelta(event.delta.x);
            const preview = computePreviewDates(task, mode, dayDelta);
            if (!preview) {
                if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
                setDragPreview(null);
                return;
            }
            if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() => {
                setDragPreview({
                    taskId,
                    mode,
                    previewStart: preview.start,
                    previewEnd: preview.end,
                });
            });
        },
        [filteredTasks, computeDayDelta, computePreviewDates]
    );

    const handleDragEnd = useCallback(
        async (event: DragEndEvent) => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            setActiveDragTaskId(null);
            setDragPreview(null);
            const dragData = event.active.data.current as any;
            if (!dragData || dragData.type !== 'gantt-bar') return;
            const taskId = String(dragData.taskId);
            const mode = dragData.mode as 'move' | 'resize-start' | 'resize-end';

            const deltaX = event.delta.x;
            const dayDelta = computeDayDelta(deltaX);
            const task = filteredTasks.find((tk) => tk.id === taskId);
            if (!task) return;

            const preview = computePreviewDates(task, mode, dayDelta);
            if (!preview) return;

            // Build the PATCH payload. Both dates always sent so the
            // optimistic update swaps both columns atomically.
            const payload: { startDate: string | null; endDate: string | null } = {
                startDate: preview.start ? preview.start.toISOString() : null,
                endDate: preview.end ? preview.end.toISOString() : null,
            };
            try {
                await updateTaskDates.mutateAsync({
                    taskId,
                    startDate: payload.startDate,
                    endDate: payload.endDate,
                    projectId,
                });
            } catch (err) {
                toast({
                    title: t('common.error'),
                    description:
                        err instanceof Error
                            ? err.message
                            : t('gantt.errorUpdateDates'),
                    variant: 'destructive',
                });
            }
        },
        [computeDayDelta, computePreviewDates, filteredTasks, projectId, updateTaskDates, toast, t]
    );

    // ----- Render -----
    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-16 text-center">
                <AlertCircle className="h-10 w-10 text-destructive mb-3" />
                <p className="text-sm text-foreground-muted">
                    {error.message || t('gantt.loadError')}
                </p>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-16">
                <Loader2 className="h-6 w-6 animate-spin text-foreground-muted" />
            </div>
        );
    }

    const draggedTask = activeDragTaskId
        ? filteredTasks.find((tk) => tk.id === activeDragTaskId) ?? null
        : null;

    // R8 / t34: compute the picker preview arrow path BEFORE the JSX
    // so we can render the SVG OUTSIDE the <Popover.Root> below.
    // Previously the SVG lived inside the Popover tree and could
    // get clipped or pushed behind other overlays depending on the
    // Popover's portal stacking. Computing the path here lets us
    // render the SVG as a sibling of the closing </Popover.Root> so
    // it lives at the top of the React tree and z-[60] always
    // wins against any popover backdrop / context menu.
    const pickPreview = (() => {
        if (
            pickState !== 'pickingTarget' ||
            !pickSourceTaskId ||
            !pickCursorPos
        ) {
            return null;
        }
        if (typeof document === 'undefined') return null;
        const barEl = document.querySelector(
            `[data-gantt-bar="${CSS.escape(pickSourceTaskId)}"]`
        );
        if (!barEl) return null;
        const rect = (barEl as HTMLElement).getBoundingClientRect();
        const sourceX = rect.left + rect.width / 2;
        const sourceY = rect.top + rect.height / 2;
        const targetX = pickCursorPos.x;
        const targetY = pickCursorPos.y;
        // R6: arrow goes from the bar's vertical-middle to the
        // cursor. A short 16px stub extends from the source so the
        // line doesn't disappear into the bar fill at its
        // mid-point. Same math as before — we just moved the
        // computation outside <Popover.Root>.
        const STUB = 16;
        const targetRightOfSource = targetX >= sourceX;
        const stubX = targetRightOfSource ? sourceX + STUB : sourceX - STUB;
        const path = `M ${sourceX} ${sourceY} L ${stubX} ${sourceY} L ${stubX} ${targetY} L ${targetX} ${targetY}`;
        return { path };
    })();

    return (
        // R8 / t34: GanttPickerPreview sits OUTSIDE the <Popover.Root>
        // so the picker arrow is a DOM-level sibling of the
        // Popover — its z-[60] always wins against any backdrop the
        // Popover renders via Portal, regardless of stacking context.
        <>
            <GanttPickerPreview
                pickState={pickState}
                pickSourceTaskId={pickSourceTaskId}
                pickCursorPos={pickCursorPos}
                pickPreview={pickPreview}
            />

            {/* R7 / t33: wrap the toolbar + settings panel in a single
                <Popover.Root> so the toolbar's cog button can be the
                <Popover.Trigger asChild>. The previous "forward a ref to
                the button + pass anchorRef.current as the Content's anchor
                prop" hack silently failed because Popper.Content reads its
                reference element from the PopperContext (set by Trigger /
                Anchor), NOT from a Content prop. Radix's natural Trigger /
                Content pattern just works. */}
            <Popover.Root>
            <div className="space-y-3">
                <GanttToolbar
                    origin={origin}
                    requestedTimeScale={effectiveConfig.timeScale}
                    timeScale={concreteTimeScale}
                    hasSprintField={hasSprintField}
                    onOriginChange={setOrigin}
                    onTimeScaleChange={(s) =>
                        handleConfigChange({ ...effectiveConfig, timeScale: s })
                    }
                />

                <DndContext
                    sensors={sensors}
                    onDragStart={handleDragStart}
                    onDragMove={handleDragMove}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                >
                    <GanttTimeline
                        origin={origin}
                        timeScale={concreteTimeScale}
                        groupBy={effectiveConfig.groupBy}
                        showSprints={effectiveConfig.showSprints && hasSprintField}
                        showDateless={effectiveConfig.showDateless}
                        customFields={customFields}
                        tasks={filteredTasks}
                        statusLabel={(s) => getStatusLabel(s, t)}
                        unassignedLabel={t('gantt.unassigned')}
                        canDrag={canEdit ?? false}
                        onTaskClick={onTaskClick}
                        dragPreview={dragPreview}
                        // R6 / t31: cursor-driven dep picker (now
                        // direction-free; the source is picked via
                        // right-click menu, predecessor/ successor
                        // are derived from the two endpoints' X
                        // order at POST time).
                        pickSourceTaskId={pickSourceTaskId}
                        pickState={pickState}
                        pickCursorPos={pickCursorPos}
                        onStartPickTarget={handleStartPickTarget}
                    />

                    <DragOverlay dropAnimation={null}>
                        {draggedTask ? (
                            <div
                                className="h-7 px-2 inline-flex items-center gap-1 rounded-md bg-primary text-primary-foreground text-[11px] font-medium shadow-lg"
                            >
                                {draggedTask.title}
                            </div>
                        ) : null}
                    </DragOverlay>
                </DndContext>

                <GanttSettingsPanel
                    config={effectiveConfig}
                    hasSprintField={hasSprintField}
                    onConfigChange={handleConfigChange}
                />
            </div>
        </Popover.Root>
        </>
    );
}

// R8 / t34: picker preview SVG rendered OUTSIDE <Popover.Root> so it
// sits at the top of the React tree. z-[60] always wins against any
// popover backdrop / context menu because nothing in the Popover
// portal can paint above an element that's a sibling of the Popover
// itself in the DOM order.
function GanttPickerPreview(props: {
    pickState: 'idle' | 'pickingTarget';
    pickSourceTaskId: string | null;
    pickCursorPos: { x: number; y: number } | null;
    pickPreview: { path: string } | null;
}) {
    const { pickState, pickSourceTaskId, pickCursorPos, pickPreview } = props;
    if (
        pickState !== 'pickingTarget' ||
        !pickSourceTaskId ||
        !pickCursorPos ||
        !pickPreview
    ) {
        return null;
    }
    return (
        <svg
            className="fixed inset-0 pointer-events-none z-[60]"
            width="100%"
            height="100%"
            data-testid="gantt-dep-preview"
            data-gantt-pick-arrow="true"
            data-pick-source={pickSourceTaskId}
            data-arrow-shape="left-to-right"
        >
            <defs>
                <marker
                    id="gantt-pick-arrow-end"
                    viewBox="0 0 8 8"
                    refX="7"
                    refY="4"
                    markerWidth="6"
                    markerHeight="6"
                    orient="auto-start-reverse"
                    markerUnits="strokeWidth"
                >
                    <path d="M 0 0 L 8 4 L 0 8 z" fill="#6366f1" />
                </marker>
            </defs>
            <path
                d={pickPreview.path}
                fill="none"
                stroke="#6366f1"
                strokeWidth={2}
                strokeDasharray="6 4"
                strokeLinejoin="miter"
                markerEnd="url(#gantt-pick-arrow-end)"
                style={{ color: '#6366f1' }}
            />
        </svg>
    );
}

export default GanttView;
